<template>
  <div :class="{ 'c-dark-theme': $store.state.darkMode }" class="c-app">
    <CContainer class="d-flex align-items-center min-vh-100 py-4">
      <CRow class="w-100 justify-content-center mx-0">
        <CCol sm="10" md="8" lg="6" xl="5" class="px-0">
          <CCard class="border-0 shadow-sm mb-0">
            <CCardBody class="p-4">
              <!-- Loading -->
              <div v-if="loading" class="text-center py-5" role="status">
                <CSpinner color="primary" />
                <p class="text-muted mt-3 mb-0">Getting your connection request ready…</p>
              </div>

              <!-- Error -->
              <div v-else-if="loadError" class="text-center py-4">
                <CIcon name="cil-warning" size="xl" class="text-warning mb-3" />
                <h1 class="h4 mb-2">We couldn't open this request</h1>
                <p class="text-muted mb-4">{{ loadError }}</p>
                <CButton color="light" class="mr-2" @click="goToDashboard">Go to dashboard</CButton>
                <CButton color="primary" @click="load">Try again</CButton>
              </div>

              <!-- Not the owner -->
              <div v-else-if="!info.isOwner || !info.businesses.length" class="text-center py-4">
                <CIcon name="cil-shield-alt" size="xl" class="text-muted mb-3" />
                <h1 class="h4 mb-2">Only the business owner can connect this</h1>
                <p class="text-muted mb-4">
                  Ask the owner of your Niagaku account to connect {{ info.clientName }}.
                </p>
                <CButton color="light" :disabled="deciding" @click="decide('deny')">Go back to {{ info.clientName }}</CButton>
              </div>

              <!-- Consent -->
              <div v-else>
                <h1 class="h4 mb-1">Connect {{ info.clientName }} to Niagaku?</h1>
                <p class="text-muted mb-4">
                  {{ info.clientName }} will be able to help with your business when you ask in chat.
                </p>

                <CSelect
                  v-if="info.businesses.length > 1"
                  label="Business"
                  :value.sync="businessId"
                  :options="businessOptions"
                  :disabled="deciding"
                />
                <p v-else class="mb-3">
                  <span class="text-muted">Business:</span> <strong>{{ info.businesses[0].name }}</strong>
                </p>

                <p class="font-weight-bold mb-2">{{ info.clientName }} will be able to:</p>
                <ul class="list-unstyled mb-3">
                  <li v-for="s in requiredScopes" :key="s.scope" class="d-flex align-items-start mb-2">
                    <CIcon name="cil-check-circle" class="text-success mr-2 mt-1 flex-shrink-0" />
                    <span>{{ s.description }}</span>
                  </li>
                </ul>

                <template v-if="optionalScopes.length">
                  <p class="font-weight-bold mb-1">Also allow, only if you tick:</p>
                  <p class="text-muted small mb-2">You can leave these off and still use everything above.</p>
                  <CInputCheckbox
                    v-for="s in optionalScopes"
                    :key="s.scope"
                    :label="s.description"
                    :checked="ticked.includes(s.scope)"
                    :disabled="deciding"
                    class="mb-2"
                    @update:checked="(on) => toggleOptional(s.scope, on)"
                  />
                </template>

                <CAlert color="info" class="mb-4 mt-3">
                  Anything it creates starts as a draft for you to check in Niagaku. This turns on the AI
                  assistant for {{ selectedBusinessName }}. You can disconnect at any time under
                  AI assistant in your account menu.
                </CAlert>

                <CAlert v-if="decideError" color="danger" class="mb-3">{{ decideError }}</CAlert>

                <div class="d-flex justify-content-end">
                  <CButton color="light" class="mr-2" :disabled="deciding" @click="decide('deny')">Cancel</CButton>
                  <CButton color="primary" :disabled="deciding || !businessId" @click="decide('allow')">
                    <CSpinner v-if="deciding" size="sm" class="mr-1" />
                    Allow access
                  </CButton>
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </CContainer>
  </div>
</template>

<script>
import AgentSettingsApi from "@/lib/agentSettingsApi";

export default {
  name: "OAuthConsent",
  data() {
    return {
      api: new AgentSettingsApi(),
      loading: true,
      loadError: "",
      info: { clientName: "the AI assistant", isOwner: false, businesses: [], scopes: [] },
      businessId: null,
      deciding: false,
      decideError: "",
      ticked: [],
    };
  },
  computed: {
    requiredScopes() {
      return this.info.scopes.filter((s) => !s.optional);
    },
    optionalScopes() {
      return this.info.scopes.filter((s) => s.optional);
    },
    businessOptions() {
      return this.info.businesses.map((b) => ({ value: b.id, label: b.name }));
    },
    selectedBusinessName() {
      const selected = this.info.businesses.find((b) => b.id === this.businessId);
      return selected ? selected.name : "this business";
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.loadError = "";
      this.api
        .getConsent(this.$route.query.client_id)
        .then((response) => {
          this.info = response.result;
          const ids = this.info.businesses.map((b) => b.id);
          this.businessId = ids.includes(this.info.defaultBusinessId) ? this.info.defaultBusinessId : ids[0] || null;
        })
        .catch((error) => {
          console.error("Failed to load consent request", error);
          this.loadError = error && error.data && error.data.statusCode === 404
            ? "This connection request has expired. Start again from your AI app."
            : "Check your connection and try again.";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    toggleOptional(scope, on) {
      this.ticked = on ? [...new Set([...this.ticked, scope])] : this.ticked.filter((s) => s !== scope);
    },
    decide(decision) {
      this.deciding = true;
      this.decideError = "";
      this.api
        .decide(this.$route.query, decision, decision === "allow" ? this.businessId : null, this.ticked)
        .then((redirectUri) => {
          window.location.assign(redirectUri);
        })
        .catch((error) => {
          console.error("Consent decision failed", error);
          const message = error && error.response && typeof error.response.data === "string" ? error.response.data : "";
          this.decideError = message && message.length < 200
            ? message
            : "We couldn't finish connecting. Try again, or start again from your AI app.";
          this.deciding = false;
        });
    },
    goToDashboard() {
      this.$router.push({ path: "/dashboard" });
    },
  },
};
</script>
